import {openSupport} from './messenger';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {connect} from 'react-redux';
import './Support.less';
import '@/stylesheets/form.less';
import '@/stylesheets/container-styles.less';
import * as OptionActions from '@/store/actions/optionActions';
import * as package_json from '../../package.json';
import Auth from "@/Auth/Auth";
import Checkbox from "@/ui/Checkbox";
import Icons, {IconType} from "@/ui/IconButton/Icons";
import IconButton from "@/ui/IconButton/IconButton";
import Loading from "@/ui/Loading";
import i18n from '@/i18n/i18n';
import ls from '@/lib/localStorage';
import type {ICPU, IDevice, IEngine, IOS} from "@ua-parser-js/pro-business";
import {UAParser} from "@ua-parser-js/pro-business";
import useIsMounted from "@/hooks/useIsMounted";
import util from '@/lib/util';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getCurrentFirm, getCurrentUser, getCurrentUserId} from "@/lib/picos/currentLogin";
import {getEnvironment as getEnv, getEnvironmentName} from "@/lib/get-environment";
import * as idb from "@/lib/idbd";
import {StoreResult} from "@/lib/idbd";
import {queueCountSlice} from "@/store/queueCount";
import User from "@/entities/user";
import Firm from "@/entities/firm";
import {RootDispatch, RootState} from "@/@types/store";
import Option from "@/entities/option";
import {LocalStorageType, useLocalStorage} from "@/hooks/useLocalStorage";
import TabBar from "@/ui/TabBar/TabBar";
import type {Tab as TabBarTab} from "@/ui/TabBar/TabBar";
import Environment from "./Pages/Environment";
import CacheManagement from "./Pages/CacheManagement";
import AdvancedDiagnostics from "./Pages/AdvancedDiagnostics";
import BadTasks from "./Pages/BadTasks";
import BadTransactions from "./Pages/BadTransactions";
import useComponentCache from "@/hooks/useComponentCache";
import deepEqual from "fast-deep-equal/es6";
import {useLocation} from "wouter";
import {getVersions, type VersionInfo} from "@/rest/api";
import {formatBuildTime, serverBuildCommit, serverReleaseTag} from "@/lib/buildIdentity";

export type SupportProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

export type EnvironmentResult = {
    version: {
        dateTimeStamp: string,
        env: string
    },
    browser: {
        userAgent: string,
        name: string,
        majorVersion: string | number,
        version: string,
        device: IDevice,
        engine: IEngine,
        os: IOS,
        cpu: ICPU,
        isSupported: boolean,
        isCurrent: boolean,
        isMinimum: boolean,
        quotaUsage: number,
        quotaTotal: number
    },
    connectivity: {
        isOnline: boolean,
        isApiServerConnected: boolean
    },
    authentication: {
        currentUser: User,
        currentFirm: Firm,
        isAdmin: boolean,
        isAuthenticated: boolean
    },
    cache: {
        isIdbCurrent: boolean,
        idbVersionCurrent: number,
        idbVersionRunning: number
    },
    storage: {
        localStorage: Record<string, any>,
        queues: StoreResult,
        queueError: any
    }
}

/**
 * Note that Chrome and Firefox update themselves on desktops, but
 * have to be manually updated on mobile unless specifically allowed
 * to auto-update.
 *
 * Edge and Safari are upgraded only when the operating system is updated,
 * both on desktop and mobile.  Luckily, most mobile Apple devices are very
 * up-to-date.  No guarantee with desktop devices, though.
 *
 * IE is simply not supported.  It is a hellhole of security vulnerabilities
 * and unsupported features.
 *
 * npx browserslist "> 2% in US" on 01/17/2018
 * and_chr 66
 * chrome 65
 * firefox 59
 * ie 11
 * ios_saf 11.3
 * ios_saf 11.0-11.2
 * ios_saf 10.3
 *
 * @type {{chrome: number, firefox: number, edge: number, safari: number}}
 */
export enum minimumVersions {
    Chrome = 124,
    Safari = 17.4,
    "Mobile Safari" = 17.4,
    Firefox = 125,
    Edge = 124,
    Opera = 99
}

export enum currentVersions {
    Chrome = 126,
    Safari = 17.5,
    "Mobile Safari" = 17.5,
    Firefox = 127,
    Edge = 126,
    Opera = 109
}

const supportedBrowsers = ['Chrome', 'Opera', 'Firefox', 'Safari', 'Mobile Safari', 'Edge'];

const getEnvironment = async (props: SupportProps): Promise<EnvironmentResult> => {
    // basic user agent parsing
    const parser = new UAParser();

    const dateTimeStamp = formatBuildTime(__BUILD_DATETIME__) ?? "";

    const idbCurrent = ls.getNumeric(ls.IDB_VERSION_CURRENT, 0);
    const idbRunning = ls.getNumeric(ls.IDB_VERSION_RUNNING, 0);
    const isIdbCurrent = idbRunning === idbCurrent;

    const mvp = parser.getBrowser().version.split('.');
    const majorVersion = mvp.length === 1 ? mvp[0] : Number(`${mvp[0]}.${mvp[1]}`);
    const minimumVersion = minimumVersions[parser.getBrowser().name];
    const currentVersion = currentVersions[parser.getBrowser().name];

    const isMinimum = majorVersion >= minimumVersion;
    const isCurrent = majorVersion >= currentVersion;
    const isSupported = !!supportedBrowsers.find(sb => parser.getBrowser().name.indexOf(sb) > -1);

    let quotaTotal = null;
    let quotaUsage = null;

    try {
        const estimate = await navigator.storage.estimate();
        quotaUsage = estimate.usage;
        quotaTotal = estimate.quota;
    }
    catch (e) {
        if (getEnv() !== 'D') console.warn(`getEnvironment: Unable to get storage estimates on ${parser.getBrowser().name}`);
    }

    let queues: StoreResult;
    let queueError: any;

    try {
        const getAllResult = await idb.getAll();
        if (getAllResult.isErr()) {
            queueError = getAllResult.unwrapErr();
            queues = {};
            console.warn('getEnvironment: Error getting queue information', getAllResult.unwrapErr());
        }
        else queues = getAllResult.unwrap();
    }
    catch (err) {
        queueError = err;
        queues = {};
        console.warn(`getEnvironment: Error getting queue information`, err.message);
    }

    // Snap the footer counter to truth so it agrees with what this page is about to display.
    const actualQueueCount = Object.entries(queues).reduce(
        (sum, [name, arr]) => sum + (Array.isArray(arr)
            ? arr.filter(item => name !== 'document' || !item.supersededByAddedOn).length : 0),
        0
    );
    queueCountSlice.set('count', actualQueueCount);

    return {
        version: {
            dateTimeStamp,
            env: getEnvironmentName()
        },
        browser: {
            userAgent: parser.getUA(),
            name: parser.getBrowser().name,
            majorVersion,
            version: parser.getBrowser().version,
            device: parser.getDevice(),
            engine: parser.getEngine(),
            os: parser.getOS(),
            cpu: parser.getCPU(),
            isSupported,
            isCurrent,
            isMinimum,
            quotaUsage,
            quotaTotal
        },
        connectivity: {
            isOnline: window.navigator.onLine,
            isApiServerConnected: props.misc.isConnected
        },
        authentication: {
            currentUser: getCurrentUser(),
            currentFirm: getCurrentFirm(),
            isAdmin: Auth.isAdmin(getCurrentUserId()),
            isAuthenticated: Auth.isAuthenticated()
        },
        cache: {
            isIdbCurrent: isIdbCurrent,
            idbVersionCurrent: idbCurrent,
            idbVersionRunning: idbRunning
        },
        storage: {
            localStorage: ls.getAll(true),
            queues,
            queueError
        }
    };
};

export type SupportPage = {
    urlKey: string;
    label: string;
    portalVisible: boolean;
    icon: IconType;
    customStyles: string;
    description: string;
    items?: string[];
};

// `portalVisible` gates each page for client-portal users (filtered by
// `visibleTabs` below and by the UserMenu Support panel). Portal users must see
// ONLY "Data and Cache" and "Environment and Installation" — every other page is
// internal/firm-only, so keep its `portalVisible: false`.
export const SUPPORT_PAGES: SupportPage[] = [
    {urlKey: 'data-and-cache', label: 'Data and Cache', portalVisible: true, description: 'Refresh data; clear caches, backups & uncommitted changes', icon: Icons.DATABASE, customStyles: 'support-tab--data-cache',
        items: ['Refresh Data', 'Clear Backups', 'Clear Caches', 'Clear Uncommitted', 'Retry Cache']},
    {urlKey: 'environment', label: 'Environment and Installation', portalVisible: true, description: 'App version, device & installation details', icon: Icons.CONFIG, customStyles: 'container--leads'},
    {urlKey: 'task-issues', label: 'Task Issues', portalVisible: false, description: 'Find and repair problem tasks', icon: Icons.TASKS, customStyles: 'container--tasks'},
    {urlKey: 'transaction-issues', label: 'Transaction Issues', portalVisible: false, description: 'Diagnose payment & billing records', icon: Icons.CREDIT_CARD, customStyles: 'container--transactions'},
    {urlKey: 'diagnostics', label: 'Advanced Diagnostics', portalVisible: false, description: 'Toggle logging for a support session', icon: Icons.CLIPBOARD, customStyles: 'support-tab--diagnostics',
        items: ['Enable Authentication Logging', 'Enable WebSocket Logging', 'Enable API Logging', 'Enable Data Logging', 'Enable Queue Logging', 'Enable Restore Logging']},
    // Session Replay and Trash were moved out to top-level UserMenu destinations
    // (/session-replay and /trash). See App.tsx routing and UserMenu.tsx.
];

const TAB_CONFIG = SUPPORT_PAGES.map(p => ({
    urlKey: p.urlKey,
    label: p.label,
    portalVisible: p.portalVisible,
    icon: p.icon.reactIcon,
    customStyles: p.customStyles,
}));

type SupportState = {
    isReady: boolean,
    environment: EnvironmentResult,
    lastTabKey: string
}

function Support(props: SupportProps) {
    const {cacheSave, cacheValue} = useComponentCache<SupportState>('support-form');

    const [showHelp, setShowHelp] = useLocalStorage<LocalStorageType.boolean>(ls.SHOW_HELP, LocalStorageType.boolean, true);

    const isMounted = useIsMounted();

    // const isDemoMode = window.__demo_mode__;

    const [location, navigate] = useLocation();

    const visibleTabs = useMemo(
        () => TAB_CONFIG.filter(t => t.portalVisible || !props.misc.portalUser),
        [props.misc.portalUser]
    );

    const defaultTabKey = useMemo(() => {
        const cached = cacheValue('lastTabKey', 'data-and-cache');
        return visibleTabs.some(t => t.urlKey === cached) ? cached : visibleTabs[0].urlKey;
    }, [visibleTabs, cacheValue]);

    const urlSegment = location.replace(/^\/support\/?/, '').replace(/\/.*$/, '');

    const selectedIndex = useMemo(
        () => Math.max(0, visibleTabs.findIndex(t => t.urlKey === urlSegment)),
        [visibleTabs, urlSegment]
    );

    const currentTabKey = visibleTabs[selectedIndex]?.urlKey;

    const onTabBarClick = useCallback((title: string) => {
        navigate(`/support/${title}`);
    }, [navigate]);

    const tabBarTabs: TabBarTab[] = useMemo(
        () => visibleTabs.map(t => ({
            title: t.urlKey,
            displayTitle: t.label,
            icon: t.icon,
            customStyles: t.customStyles,
        })),
        [visibleTabs]
    );

    // Redirect bare /support or invalid tab key to the default tab
    useEffect(() => {
        if (!urlSegment || !visibleTabs.some(t => t.urlKey === urlSegment)) {
            navigate(`/support/${defaultTabKey}`, {replace: true});
        }
    }, [urlSegment, visibleTabs, defaultTabKey, navigate]);

    const [isReady, setIsReady] = useState<boolean>(cacheValue('isReady', false));
    const [environment, setEnvironment] = useState<EnvironmentResult>(cacheValue('environment', null));
    const [serverVersionInfo, setServerVersionInfo] = useState<VersionInfo | null>(null);

    // Save state
    useEffect(() => {
        return () => cacheSave({isReady, environment, lastTabKey: currentTabKey});
    }, [isReady, environment, currentTabKey, cacheSave]);

    // These are used
    let stateTimeout = useRef<NodeJS.Timeout>(null);
    let environmentInterval = useRef<NodeJS.Timeout>(null);

    const updateOnline = useCallback(() => {
        const env = {...environment};
        env.connectivity.isOnline = window.navigator.onLine;
        setEnvironment(env);
    }, [environment, setEnvironment]);

    const reloadEnvironmentTimeoutHandler = useCallback((env: any) => {
        if (isMounted()) {
            if (!env || !deepEqual(env, environment)) setEnvironment(env);
            setIsReady(true);
        }
    }, [environment, isMounted, setEnvironment, setIsReady]);

    const reloadEnvironment = useCallback(() => {
        getEnvironment(props)
            .then(env => {
                clearTimeout(stateTimeout.current);
                stateTimeout.current = setTimeout(reloadEnvironmentTimeoutHandler, 10, env);
            })
            .catch(err => {
                console.error('reloadEnvironment', err.message);
            });
    }, [stateTimeout, props, reloadEnvironmentTimeoutHandler]);

    /**
     * Stop an timeouts or intervals before freeze
     */
    const prepareForFreeze = useCallback(() => {
        clearInterval(environmentInterval.current);
        environmentInterval.current = null;

        clearTimeout(stateTimeout.current);
        stateTimeout.current = null;
    }, [environmentInterval, stateTimeout]);

    /**
     * Start back up the environment interval after reinitialization
     */
    const reInitializeApp = useCallback(() => {
        // Clear first: a live handle here is unreachable once overwritten, and
        // an orphaned interval keeps calling getEnvironment() forever, including
        // after the page has been navigated away from.
        if (environmentInterval.current) clearInterval(environmentInterval.current);

        environmentInterval.current = setInterval(reloadEnvironment, 2000);
    }, [environmentInterval, reloadEnvironment]);

    /**
     * Handle changes in visibility (when a tab gains/loses focus)
     */
    const handleVisibilityChange = useCallback(() => {
        const {hidden} = util.getVisibilityInfo();

        if (document[hidden]) prepareForFreeze();
        else reInitializeApp();
    }, [prepareForFreeze, reInitializeApp]);

    /**
     * Start the environmentInterval, and clean it up
     */
    useEffect(() => {
        // Set up intervals
        if (!environmentInterval.current) environmentInterval.current = setInterval(reloadEnvironment, 5000);

        return () => {
            // Null the refs as well as clearing them. This effect re-runs
            // whenever reloadEnvironment changes identity, which it does on
            // every environment update the poll itself produces. Leaving a dead
            // handle in the ref satisfies the `if (!environmentInterval.current)`
            // guard above, so polling stopped for good after the first update.
            if (environmentInterval.current) clearInterval(environmentInterval.current);
            environmentInterval.current = null;

            if (stateTimeout.current) clearTimeout(stateTimeout.current);
            stateTimeout.current = null;
        }
    }, [reloadEnvironment]);

    /**
     * Set up and tear down listeners
     */
    useEffect(() => {
        // Add event handlers
        window.addEventListener('offline', updateOnline);
        window.addEventListener('online', updateOnline);

        // Handle page visibility change
        const {visibilityChange} = util.getVisibilityInfo();
        document.addEventListener(visibilityChange, handleVisibilityChange, false);

        return () => {
            // Remove event handlers
            window.removeEventListener('offline', updateOnline);
            window.removeEventListener('online', updateOnline);

            // Remove page visibility change handler
            const {visibilityChange} = util.getVisibilityInfo();
            document.removeEventListener(visibilityChange, handleVisibilityChange);
        }
    }, [environmentInterval, handleVisibilityChange, reloadEnvironment, stateTimeout, updateOnline]);

    // Any changes to props should update the environment
    useEffect(() => {
        reloadEnvironment();
    }, [reloadEnvironment]);

    // Fetch server version on mount and when the connection recovers.
    useEffect(() => {
        getVersions().then(result => {
            if (result.isOk() && isMounted()) {
                setServerVersionInfo(result.unwrap());
            }
        });
    }, [isMounted, props.misc.isConnected]);

    // const onDemoMode = useCallback(() => {
    //     window.__demo_mode__ = !isDemoMode;
    //     setIsDemoMode(n => !n);
    // }, [isDemoMode]);

    if (isReady) {
        const timestamp = environment.version.dateTimeStamp;

        return (
            <div className="support-form Form">
                <div className='support-form__title'><FontAwesomeIcon icon={Icons.SUPPORT.reactIcon}/>&nbsp;Support
                </div>

                <div className="support-form__content">
                    <nav className="support-form__actions" aria-label="Support options">
                        <IconButton type={Icons.COMMENTS} label="Start a support conversation" onClick={() => openSupport({newConversation: true})}/>
                        <IconButton type={Icons.FLAG} label="Report a bug or request a feature" onClick={() => openSupport({feedback: 'bug'})}/>
                        <IconButton type={Icons.SEARCH} label="Search documentation" onClick={() => openSupport({help: true})}/>
                        <IconButton type={Icons.EMAIL} label="Email support" onClick={() => { window.location.href = 'mailto:support@outlawpractice.com'; }}/>
                    </nav>

                    {/* V E R S I O N */}
                    <section id='build-info'>
                        <article className='Row'>
                            <strong>Client Version</strong>&nbsp;{package_json.version} <code>[{__BUILD_GIT_HASH__}]</code>, built at {timestamp}, running in {getEnvironmentName()}.
                        </article>

                        <article className='Row'>
                            <strong>Server Version</strong>&nbsp;{serverVersionInfo ? (
                                // The client line's shape: "<version> [<commit>], built at <local time>" (OWA-1304)
                                <>
                                    {serverVersionInfo.server}
                                    {serverBuildCommit(serverVersionInfo) ? <> <code>[{serverBuildCommit(serverVersionInfo)}]</code></> : null}
                                    {serverReleaseTag(serverVersionInfo) ? <>, release {serverReleaseTag(serverVersionInfo)}</> : null}
                                    {formatBuildTime(serverVersionInfo.serverBuiltAt) ? <>, built at {formatBuildTime(serverVersionInfo.serverBuiltAt)}</> : null}.
                                </>
                            ) : 'Unavailable'}
                        </article>
                    </section>

                    {/* D E M O   M O D E */}
                    {/*<div className="Row support-form__buttons">*/}
                    {/*	{element.renderElement('demoMode', demoModeLabel, (*/}
                    {/*		<Checkbox onClick={onDemoMode} checked={isDemoMode || false}/>*/}
                    {/*	))}*/}
                    {/*</div>*/}

                    {/* S H O W   H E L P */}
                    <section id='show-help' style={{marginTop: '1em'}}>
                        <article>
                            <Checkbox checked={showHelp}
                                      onClick={() => setShowHelp(!showHelp)}
                                      label={i18n.l('config', 'showHelp')}
                            />
                            <aside>{i18n.d('config', 'showHelp')}</aside>
                        </article>
                    </section>

                    {/* T A B S */}
                    <section className='support-form__tabs'>
                        <TabBar tabs={tabBarTabs}
                                onClick={onTabBarClick}
                                name='support-tab-bar'
                                selectedIndex={selectedIndex}
                        />
                        <div className='support-form__tab-content'>
                            {currentTabKey === 'data-and-cache' && <CacheManagement environment={environment}/>}
                            {currentTabKey === 'environment' && <Environment environment={environment}/>}
                            {currentTabKey === 'task-issues' && <BadTasks/>}
                            {currentTabKey === 'transaction-issues' && <BadTransactions/>}
                            {currentTabKey === 'diagnostics' && <AdvancedDiagnostics/>}
                        </div>
                    </section>

                </div>
            </div>
        );
    }
    else {
        return (
            <div className='support-form'>
                <Loading/>
            </div>
        );
    }
}

function mapStateToProps(state: RootState) {
    return {
        misc: state.misc,
        firms: state.firms,
        options: state.options,
        users: state.users,
        contacts: state.contacts,
    };
}

const mapDispatchToProps = (dispatch: RootDispatch) => {
    return {
        onSaveOptionQueue: (option: Option) => dispatch(OptionActions.saveOptionQueue(option))
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Support);
